import React from 'react';
import { Link } from 'react-router-dom';
import FundWiseLogo from './FundWiseLogo';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <FundWiseLogo size="md" variant="default" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-600 mb-8">Effective Date: October 4, 2025 | Last Updated: October 4, 2025</p>

          <div className="space-y-8">
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">1. Introduction</h2>
              
              <p className="text-slate-700 leading-relaxed">
                Portfolio Auditor ("we," "us," or "our") is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our portfolio analysis software and related services through our website.
              </p>
              
              <p className="text-slate-700 leading-relaxed">
                <strong className="text-slate-900">Key Privacy Feature:</strong> Our analysis tool runs entirely on your local PC using Microsoft Power BI. Your financial data never leaves your computer, ensuring complete privacy and control.
              </p>
              
              <p className="text-slate-700 leading-relaxed">
                By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use the Service.
              </p>
            </section>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-6">
              <h3 className="text-green-800 font-semibold mb-2">🔒 Complete Privacy Guarantee</h3>
              <p className="text-green-700 text-sm mb-0">
                Unlike other portfolio analysis services, Portfolio Auditor processes your CAMS statements locally on your PC. Your financial data never uploads to our servers or any third-party service.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-slate-900">Contact Information:</h3>
              <ul className="space-y-1 text-slate-700">
                <li><strong className="text-slate-900">Website:</strong> {process.env.REACT_APP_COMPANY_WEBSITE || 'www.portfolioauditor.com'}</li>
                <li><strong className="text-slate-900">Email:</strong> {process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}</li>
                <li><strong className="text-slate-900">Address:</strong> Bangalore, India</li>
              </ul>
            </div>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">2. Information We Collect</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">2.1 Personal Information</h3>
                <p className="text-slate-700 leading-relaxed">We collect the following types of personal information through our website:</p>
                
                <div className="ml-4 space-y-4">
                  <div>
                    <h4 className="text-lg font-medium text-slate-900 mb-2">Account Registration:</h4>
                    <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                      <li>Name</li>
                      <li>Email address</li>
                      <li>Password (encrypted and stored securely)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-slate-900 mb-2">Subscription Management:</h4>
                    <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                      <li>Subscription plan details (Beta - Free, Monthly Plan - ₹{process.env.REACT_APP_INDIVIDUAL_PRICE || '99'}/month, Family - ₹{process.env.REACT_APP_FAMILY_PRICE || '199'}/month, Yearly Plan - ₹{process.env.REACT_APP_ANNUAL_PRICE || '699'}/year)</li>
                      <li>License key information</li>
                      <li>Payment transaction records</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-slate-900 mb-2">License Activation Data:</h4>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-orange-800 text-sm">
                        <strong>License Security:</strong> When you activate your license on our application, we collect your Hardware ID and PAN number in encrypted form. This mapping prevents license abuse and ensures your license key is securely tied to your PC for legitimate use only.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-slate-900 mb-2">Financial Data Processing:</h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-800">
                        <strong>IMPORTANT:</strong> Your CAMS PDF statements and financial data are processed entirely on YOUR PC. We do not collect, store, or have access to your financial information. The Portfolio Auditor software downloads to your computer and processes data locally.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">2.2 Automatically Collected Information</h3>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-slate-900 mb-2">Website Analytics:</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                    <li>Session data for login authentication</li>
                    <li>Basic usage analytics (pages visited, time spent)</li>
                    <li>Device and browser information for compatibility</li>
                  </ul>
                </div>
              </div>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">3. How We Use Your Information</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">3.1 Service Provision</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li><strong className="text-slate-900">Account Management:</strong> Maintain your user account and subscription status</li>
                  <li><strong className="text-slate-900">Software Delivery:</strong> Provide download links and license keys for the Portfolio Auditor analysis tool</li>
                  <li><strong className="text-slate-900">Customer Support:</strong> Respond to your inquiries and provide technical assistance</li>
                  <li><strong className="text-slate-900">Service Updates:</strong> Notify you about software updates and new features</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">3.2 Local Data Processing</h3>
                <p className="text-slate-700 leading-relaxed">
                  The Portfolio Auditor software that you download processes your financial data locally on your PC using:
                </p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Microsoft Power BI technology for portfolio analysis</li>
                  <li>Local algorithms for benchmark comparison</li>
                  <li>Your uploaded CAMS statements (processed locally, never transmitted)</li>
                </ul>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">4. Data Sharing and Disclosure</h2>
            
              <h3 className="text-xl font-semibold text-slate-900">4.1 No Financial Data Sharing</h3>
            <p className="bg-green-50 border border-green-200 rounded-lg p-4">
              <strong>Zero Data Sharing:</strong> Since your financial data is processed locally on your PC, we never receive, store, or share your investment information with anyone.
            </p>

              <h3 className="text-xl font-semibold text-slate-900">4.2 Limited Information Sharing</h3>
              <p className="text-slate-700 leading-relaxed">We may share non-financial personal information only in these circumstances:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong className="text-slate-900">Legal Requirements:</strong> If required by law or to protect our rights</li>
                <li><strong className="text-slate-900">Business Transfers:</strong> In case of merger or acquisition (with notice)</li>
                <li><strong className="text-slate-900">Service Providers:</strong> Payment processors for subscription management (encrypted data only)</li>
              </ul>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">5. Your Rights and Choices</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">5.1 Data Subject Rights</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li><strong className="text-slate-900">Access:</strong> Request information about data we hold about you</li>
                  <li><strong className="text-slate-900">Correction:</strong> Update incorrect account information</li>
                  <li><strong className="text-slate-900">Deletion:</strong> Delete your account and associated data</li>
                  <li><strong className="text-slate-900">Data Portability:</strong> Export your account data</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">5.2 Local Data Control</h3>
                <p className="text-slate-700 leading-relaxed">
                  Since your financial data stays on your PC, you have complete control:
                </p>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>Delete CAMS statements from your computer anytime</li>
                  <li>Uninstall the Portfolio Auditor software to remove all analysis data</li>
                  <li>No need to request data deletion from us - it's already on your PC only</li>
                </ul>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">6. Data Security</h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">6.1 Website Security</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                  <li>HTTPS encryption for all data transmission</li>
                  <li>Secure password storage with encryption</li>
                  <li>Regular security updates and monitoring</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">6.2 Financial Data Security</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800">
                    <strong>Ultimate Security:</strong> Your financial data is as secure as your own PC since it never leaves your computer. This is the highest level of financial data protection possible.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">7. Data Retention</h2>
              
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li><strong className="text-slate-900">Account Data:</strong> Retained until you request deletion</li>
                <li><strong className="text-slate-900">Subscription Records:</strong> Kept for tax and legal compliance (typically 7 years)</li>
                <li><strong className="text-slate-900">Financial Data:</strong> Not applicable - stored only on your PC</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">8. International Users</h2>
              <p className="text-slate-700 leading-relaxed">
                Our website is accessible globally. The Portfolio Auditor software is designed primarily for Indian mutual fund investors but can be used internationally. Local data processing ensures compliance with most international privacy regulations.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">9. Children's Privacy</h2>
              <p className="text-slate-700 leading-relaxed">
                Our Service is intended for adults (18+) and we do not knowingly collect information from children under 18.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">10. Beta Program</h2>
              <p className="text-slate-700 leading-relaxed">
                During our Beta phase, we may collect anonymous usage feedback to improve the software. This does not include any financial data or personal investment information.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">11. Changes to This Privacy Policy</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update this Privacy Policy to reflect changes in our practices. We will notify you by:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li>Posting the updated policy on our website</li>
                <li>Sending email notifications for significant changes</li>
                <li>Updating the "Last Updated" date</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">12. Contact Us</h2>
              <p className="text-slate-700 leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                <li><strong className="text-slate-900">Email:</strong> {process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}</li>
                <li><strong className="text-slate-900">Website:</strong> {process.env.REACT_APP_COMPANY_WEBSITE || 'www.portfolioauditor.com'}</li>
              </ul>
            </section>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mt-8">
              <h3 className="text-slate-900 font-semibold mb-2">Privacy Summary</h3>
              <p className="text-slate-700 text-sm mb-0">
                Portfolio Auditor is designed with privacy as the top priority. Your financial data processing happens entirely on your PC, giving you complete control and the highest level of privacy protection possible in portfolio analysis software.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;